import React from 'react';
import Slider from '@mui/material/Slider';
import { Box, Button, Stack, TextField, Tooltip, Typography } from '../../ui/core';
import { METERS_PER_MILE } from './laneEligibility';

// This 25000 max is silently coupled to the maxRadiusMeters passed to
// fetchLaneStations in LaneMap.js (one call per operational route in the
// selected pair). They match today, so the usable-station set stays complete
// right up to this slider's maximum — but raising this max without also
// raising that fetch's radius would make stations beyond 25 km silently
// vanish from the per-pair station set. Keep both in sync.
//
// Exactly one row per LIMIT_KEYS entry (laneEligibility.js) — the fee-related
// limits (alongRouteFeeCents, minOffRouteFeeCents, costPerMileIndexCents) and
// the nearest-N station cap (stationLimit) are no longer editable here; this
// page carries no pricing and the cap UI is retired product-side.
const METER_LIMITS = [
  { key: 'stationToPolylineMeters', label: 'Station → polyline', max: 25000 },
  { key: 'farCityExclusionMeters', label: 'Far-city exclusion', max: 60000 },
  { key: 'sharedDoorRadiusMeters', label: 'Shared door radius', max: 25000 },
  // Renamed off fee language: this bound gates whether private door is
  // offered at all (see isWithinSharedDoorRadius in laneEligibility.js), not
  // a fee tier — the page carries no dollar figures any more.
  { key: 'alongRouteBoundMeters', label: 'Along-route bound', max: 25000 },
];

const SOURCE_NOTES = {
  constant: 'Code constant in hitch-services.',
  growthbook: 'GrowthBook flag with no rules — one value everywhere.',
  'growthbook-experiment':
    'GrowthBook flag driven by running experiments — there is no single production value.',
};

const sourceBadge = (source) => {
  if (!source) return null;
  return (
    <Tooltip title={SOURCE_NOTES[source] || source}>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
        {source === 'growthbook-experiment' ? 'experiment' : source}
      </Typography>
    </Tooltip>
  );
};

const LimitRow = ({ label, value, liveValue, source, max, step, onChange, suffix }) => {
  const isOverridden = liveValue !== undefined && value !== liveValue;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          {label}
          {sourceBadge(source)}
        </Typography>
        <TextField
          size="small"
          type="number"
          value={value}
          onChange={(e) => {
            const parsed = Number(e.target.value);
            // A bare "-" parses to NaN (an empty string parses to 0, not
            // NaN). Letting a NaN through would land a literal NaN in state
            // and, because serializeLimits' `liveLimits[key] === value`
            // equality check is never true for NaN, a bare `key=NaN` in the
            // URL too. A negative value is rejected for a different reason:
            // deserializeLimits already refuses negatives on read (falling
            // back to the live value), so permitting one here would let the
            // write path put a URL in place that silently fails to
            // reproduce on reload or share — and for the corridor radius
            // field specifically, a negative value would make every station
            // fail the gate and render an all-grey map. Drop both cases
            // silently rather than committing — the field itself keeps
            // whatever the user is mid-typing since `value` here is only
            // read from `limits`, not from this local parse. 0 is a real,
            // finite value and must still pass through.
            if (!Number.isFinite(parsed) || parsed < 0) return;
            onChange(parsed);
          }}
          sx={{ width: 96 }}
        />
      </Stack>
      <Slider
        size="small"
        value={value}
        min={0}
        max={max}
        step={step}
        onChange={(_e, v) => onChange(v)}
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {suffix ? suffix(value) : null}
        </Typography>
        {isOverridden && (
          <Button size="small" onClick={() => onChange(liveValue)}>
            Reset to live ({liveValue})
          </Button>
        )}
      </Stack>
    </Box>
  );
};

/**
 * Editable eligibility limits. Values are view-local: they live in React state
 * and the URL, and are never written to the backend or to GrowthBook. Exactly
 * four rows, matching LIMIT_KEYS in laneEligibility.js — keep the two in sync.
 */
const LaneMapLimitsPanel = ({ limits, sources = {}, liveLimits = {}, onChange, onResetAll }) => {
  if (!limits) return null;

  const set = (key) => (value) => onChange({ ...limits, [key]: value });
  const inMiles = (meters) => `${(meters / METERS_PER_MILE).toFixed(2)} mi`;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Limits (simulation only)
        </Typography>
        <Button size="small" onClick={onResetAll}>
          Reset all
        </Button>
      </Stack>

      {METER_LIMITS.map(({ key, label, max }) => (
        <LimitRow
          key={key}
          label={label}
          value={limits[key]}
          liveValue={liveLimits[key]}
          source={sources[key]}
          max={max}
          step={100}
          onChange={set(key)}
          suffix={inMiles}
        />
      ))}

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          onClick={() => onChange({ ...limits, sharedDoorRadiusMeters: 10000 })}
        >
          Shared radius → 10 km (pre-tightening)
        </Button>
      </Stack>
    </Box>
  );
};

export default LaneMapLimitsPanel;
